import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { usePermissions } from "../../App";
import {
  DollarSign, TrendingUp, TrendingDown, AlertTriangle, ArrowUpRight,
  ArrowDownRight, RefreshCw, Link2, Settings, BarChart3,
  FileText, Wallet, CreditCard, PieChart, LineChart, Receipt, Activity
} from "lucide-react";
import ExecutiveOverview from "./ExecutiveOverview";
import CashTab from "./CashTab";
import AccountsReceivable from "./AccountsReceivable";
import AccountsPayable from "./AccountsPayable";
import InvoicesTab from "./InvoicesTab";
import SyncHealth from "./SyncHealth";
import ComingSoonTab from "./ComingSoonTab";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TABS = [
  { id: "overview", label: "Executive Overview", icon: BarChart3 },
  { id: "cash", label: "Cash", icon: Wallet },
  { id: "ar", label: "Accounts Receivable", icon: ArrowUpRight },
  { id: "ap", label: "Accounts Payable", icon: ArrowDownRight },
  { id: "projects", label: "Projects & Margins", icon: PieChart },
  { id: "revenue", label: "Revenue", icon: TrendingUp },
  { id: "expenses", label: "Expenses / Overhead", icon: CreditCard },
  { id: "forecast", label: "Forecast", icon: LineChart },
  { id: "invoices", label: "Invoices", icon: Receipt },
  { id: "settings", label: "Sync Health", icon: Activity },
];

export default function FinancialsView() {
  const { canViewFinancials, loading: permsLoading } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "overview");
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchConnection = useCallback(async () => {
    try {
      const res = await fetch(`${API}/quickbooks/connection-status`, { headers });
      const data = await res.json();
      setConnection(data);
    } catch {
      setConnection({ connected: false, status: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnection();
    // Check for OAuth callback params
    const qbConnected = searchParams.get("qb_connected");
    const qbError = searchParams.get("qb_error");
    if (qbConnected === "true") {
      toast.success("QuickBooks connected successfully!");
      searchParams.delete("qb_connected");
      setSearchParams(searchParams);
      fetchConnection();
    }
    if (qbError) {
      toast.error(`QuickBooks connection failed: ${qbError}`);
      searchParams.delete("qb_error");
      setSearchParams(searchParams);
    }
  }, []);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const handleConnect = () => {
    window.location.href = `${API}/quickbooks/authorize`;
  };

  const handleSync = async () => {
    try {
      await fetch(`${API}/quickbooks/sync`, { method: "POST", headers });
      toast.success("Sync started — data will refresh shortly");
      setTimeout(fetchConnection, 3000);
    } catch {
      toast.error("Failed to start sync");
    }
  };

  // Financial lock: PMs / non-financial roles cannot open this page, even by
  // typing the URL directly (the sidebar link is already hidden for them).
  if (permsLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#000000]">
        <RefreshCw className="w-8 h-8 text-white/40 animate-spin" />
      </div>
    );
  }
  if (!canViewFinancials) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#000000]">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 text-white/40 animate-spin" />
          <p className="text-white/50 text-sm tracking-wide">Loading Financials...</p>
        </div>
      </div>
    );
  }

  // Not connected — show connection screen
  if (!connection?.connected) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center p-8">
        <div className="max-w-lg w-full">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/[0.04] border border-white/[0.06] mb-6">
              <DollarSign className="w-8 h-8 text-white/70" />
            </div>
            <h1 className="text-3xl font-light text-white tracking-tight mb-3">
              Financial Command Center
            </h1>
            <p className="text-white/50 text-base leading-relaxed max-w-md mx-auto">
              Connect your QuickBooks Online account to get a real-time executive view of cash, receivables, payables, and profitability.
            </p>
          </div>

          <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8">
            <div className="space-y-4 mb-8">
              {["Cash & bank balances", "Accounts receivable aging", "Accounts payable tracking", "Executive KPI dashboard", "Sync health monitoring"].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500/80" />
                  <span className="text-white/60 text-sm">{item}</span>
                </div>
              ))}
            </div>
            <button
              onClick={handleConnect}
              data-testid="connect-quickbooks-btn"
              className="w-full py-3.5 bg-white text-black font-medium rounded-xl hover:bg-white/90 transition-all text-sm tracking-wide"
            >
              Connect QuickBooks Online
            </button>
            {connection?.status === "reauth_required" && (
              <p className="text-amber-400/80 text-xs mt-3 text-center">
                Previous connection expired. Please reconnect.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Connected — show full financial dashboard
  return (
    <div className="min-h-screen bg-[#000000]" data-testid="financials-dashboard">
      {/* Header */}
      <div className="border-b border-white/[0.06] bg-[#000000] sticky top-0 z-30">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-white tracking-tight">Financials</h1>
            <span className="text-[10px] font-medium text-emerald-400/80 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
              {connection.company_name}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {connection.last_sync && (
              <span className="text-[11px] text-white/30 mr-2">
                Last sync: {new Date(connection.last_sync).toLocaleString()}
              </span>
            )}
            <button
              onClick={handleSync}
              data-testid="sync-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white/70 bg-white/[0.04] border border-white/[0.08] rounded-lg hover:bg-white/[0.08] transition-all"
            >
              <RefreshCw className="w-3 h-3" />
              Sync
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="px-6 flex gap-0 overflow-x-auto scrollbar-hide">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`
                  flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-all
                  ${isActive
                    ? "text-white border-white"
                    : "text-white/40 border-transparent hover:text-white/60 hover:border-white/10"}
                `}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="p-6">
        {activeTab === "overview" && <ExecutiveOverview connection={connection} />}
        {activeTab === "cash" && <CashTab />}
        {activeTab === "ar" && <AccountsReceivable />}
        {activeTab === "ap" && <AccountsPayable />}
        {activeTab === "invoices" && <InvoicesTab />}
        {activeTab === "settings" && <SyncHealth connection={connection} onSync={handleSync} onReconnect={handleConnect} onRefresh={fetchConnection} />}
        {["projects", "revenue", "expenses", "forecast"].includes(activeTab) && (
          <ComingSoonTab tabName={TABS.find(t => t.id === activeTab)?.label || activeTab} />
        )}
      </div>
    </div>
  );
}
